import React from 'react'

type SkeletonVariant = 'fixtures' | 'leagues' | 'picks' | 'alerts' | 'highlights' | 'trending'

interface WidgetSkeletonProps {
  variant: SkeletonVariant
}

function SkeletonLine({ width = '100%', height = '1rem' }: { width?: string; height?: string }) {
  return <div className="widget-skeleton-line" style={{ width, height }} />
}

function SkeletonCircle({ size = '2rem' }: { size?: string }) {
  return <div className="widget-skeleton-circle" style={{ width: size, height: size }} />
}

function FixturesSkeleton() {
  return (
    <div className="widget-skeleton-fixtures">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="widget-skeleton-fixture-item">
          <div className="widget-skeleton-fixture-teams">
            <SkeletonLine width="35%" height="0.875rem" />
            <SkeletonLine width="1.5rem" height="0.75rem" />
            <SkeletonLine width="35%" height="0.875rem" />
          </div>
          <div className="widget-skeleton-fixture-info">
            <SkeletonLine width="3rem" height="0.75rem" />
          </div>
        </div>
      ))}
    </div>
  )
}

function LeaguesSkeleton() {
  return (
    <div className="widget-skeleton-leagues">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="widget-skeleton-league-item">
          <SkeletonCircle size="2.5rem" />
          <SkeletonLine width="80%" height="0.625rem" />
        </div>
      ))}
    </div>
  )
}

function PicksSkeleton() {
  return (
    <div className="widget-skeleton-picks">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="widget-skeleton-pick-item">
          <div className="widget-skeleton-pick-match">
            <SkeletonLine width="70%" height="0.875rem" />
            <SkeletonLine width="4rem" height="0.625rem" />
          </div>
          <div className="widget-skeleton-pick-details">
            <SkeletonLine width="40%" height="0.75rem" />
            <SkeletonLine width="2.5rem" height="0.875rem" />
            <SkeletonLine width="3rem" height="0.75rem" />
          </div>
        </div>
      ))}
    </div>
  )
}

function AlertsSkeleton() {
  return (
    <div className="widget-skeleton-alerts">
      {[1, 2, 3].map((i) => (
        <div key={i} className="widget-skeleton-alert-item">
          <div className="widget-skeleton-alert-info">
            <SkeletonLine width="60%" height="0.875rem" />
            <SkeletonLine width="2.5rem" height="0.75rem" />
          </div>
          <div className="widget-skeleton-alert-meta">
            <SkeletonLine width="4rem" height="0.625rem" />
          </div>
        </div>
      ))}
    </div>
  )
}

function HighlightsSkeleton() {
  return (
    <div className="widget-skeleton-highlights">
      {[1, 2, 3].map((i) => (
        <div key={i} className="widget-skeleton-highlight-item">
          <div className="widget-skeleton-highlight-match">
            <SkeletonLine width="65%" height="0.875rem" />
          </div>
          <div className="widget-skeleton-highlight-details">
            <SkeletonLine width="50%" height="0.75rem" />
            <SkeletonLine width="3.5rem" height="1rem" />
          </div>
        </div>
      ))}
    </div>
  )
}

function TrendingSkeleton() {
  return (
    <div className="widget-skeleton-trending">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="widget-skeleton-trending-item">
          <SkeletonCircle size="2rem" />
          <SkeletonLine width="80%" height="0.625rem" />
          <div className="widget-skeleton-trending-form">
            {[1, 2, 3, 4, 5].map((j) => (
              <SkeletonCircle key={j} size="0.5rem" />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function WidgetSkeleton({ variant }: WidgetSkeletonProps) {
  switch (variant) {
    case 'fixtures':
      return <FixturesSkeleton />
    case 'leagues':
      return <LeaguesSkeleton />
    case 'picks':
      return <PicksSkeleton />
    case 'alerts':
      return <AlertsSkeleton />
    case 'highlights':
      return <HighlightsSkeleton />
    case 'trending':
      return <TrendingSkeleton />
    default:
      return (
        <div className="widget-skeleton-default">
          <SkeletonLine width="100%" height="1rem" />
          <SkeletonLine width="80%" height="1rem" />
          <SkeletonLine width="60%" height="1rem" />
        </div>
      )
  }
}
